import React, { useEffect, useState } from "react";
import {
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Chip,
  Alert,
} from "@mui/material";
import RegisterHeader from "./Common/Header";
import RegisterSteps from "./Common/Steps";
import { useNavigate } from "react-router-dom";
import { useRegister } from "../../context/RegisterContext";
import { PlanCategory } from "../../types/RegisterType";
import { savePreRegistration } from "../../utils/preRegistration";
import Footer from "../Footer";
import ReactGA from "react-ga4";
import "./Register.scss";

interface CategoryCardProps {
  category: PlanCategory;
  isSelected: boolean;
  onSelect: () => void;
  recommended?: boolean;
}

const HeatIndicator: React.FC<{ level: 1 | 2 | 3 | 4 }> = ({ level }) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          style={{
            height: 8,
            width: 24,
            borderRadius: 4,
            backgroundColor: i <= level ? '#10b981' : '#e2e8f0',
            transition: 'all 0.3s ease',
          }}
        />
      ))}
    </div>
  );
};

const CATEGORY_DISPLAY_CONFIGS = [
  { keywords: ['básico', 'flex'], volume: 'Alto', perfil: 'Busca conveniência', heat: 4 as const, color: '#1976d2' },
  { keywords: ['premium', 'prime'], volume: 'Médio-Alto', perfil: 'Qualidade/preço', heat: 3 as const, color: '#2e7d32' },
  { keywords: ['elite', 'vip'], volume: 'Médio', perfil: 'Premium', heat: 2 as const, color: '#10b981' },
  { keywords: ['ultra'], volume: 'Baixo', perfil: 'Menos volume, ticket alto por cliente', heat: 1 as const, color: '#1976d2' },
];

const CategoryCard: React.FC<CategoryCardProps> = ({
  category,
  isSelected,
  onSelect,
  recommended = false,
}) => {
  const nameLower = category.name.toLowerCase();
  const matched = CATEGORY_DISPLAY_CONFIGS.find(config =>
    config.keywords.some(kw => nameLower.includes(kw))
  );
  const categoryData = {
    repasse: category.transfer_value != null ? `R$ ${category.transfer_value}` : 'A consultar',
    volume: matched?.volume ?? 'Médio',
    perfil: matched?.perfil ?? (category.description || 'Categoria personalizada'),
    heat: matched?.heat ?? (3 as const),
    color: matched?.color ?? '#1976d2',
  };

  return (
    <Card
      sx={{
        cursor: 'pointer',
        border: isSelected ? `2px solid ${categoryData.color}` : recommended ? '2px solid #1976d2' : '2px solid #e2e8f0',
        borderRadius: 3,
        overflow: 'initial',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: categoryData.color,
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        },
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isSelected ? `${categoryData.color}08` : 'white',
      }}
      onClick={onSelect}
    >
      {recommended && (
        <Chip
          label="Recomendado"
          color="primary"
          size="small"
          sx={{
            position: 'absolute',
            top: -10,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            backgroundColor: '#1976d2',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.7  5rem',
            height: '24px',
          }}
        />
      )}
      
      <CardContent sx={{ padding: 3, flexGrow: 1, display: 'flex', flexDirection: 'column'}}>
        {/* Header com categoria e indicador de calor */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, textAlign: 'left', height: '50px' }}>
          <Box>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', color: 'text.secondary', fontWeight: 'medium' }}>
              Categoria
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              {category.name}
            </Typography>
          </Box>
          <HeatIndicator level={categoryData.heat} />
        </Box>

        {/* Grid com repasse e volume */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Box sx={{ 
              backgroundColor: '#f8fafc', 
              border: '1px solid #e2e8f0', 
              borderRadius: 2, 
              padding: 2,
              height: '70px'
            }}>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', color: 'text.secondary', fontWeight: 'medium' }}>
                Repasse de.
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                {categoryData.repasse}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ 
              backgroundColor: '#f8fafc', 
              border: '1px solid #e2e8f0', 
              borderRadius: 2, 
              padding: 2,
              height: '70px'
            }}>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', color: 'text.secondary', fontWeight: 'medium' }}>
                Volume
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                {categoryData.volume}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Perfil */}
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, flexGrow: 1, lineHeight: 1.6 }}>
          {categoryData.perfil}
        </Typography>

        {/* Botão */}
        <Button
          variant={isSelected ? "contained" : "outlined"}
          fullWidth
          sx={{
            backgroundColor: isSelected ? '#10b981' : recommended ? '#1976d2' : 'transparent',
            borderColor: isSelected ? '#10b981' : recommended ? '#1976d2' : '#1976d2',
            color: isSelected || recommended ? 'white' : '#1976d2',
            fontWeight: 'bold',
            padding: '12px',
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '0.95rem',
            '&:hover': {
              backgroundColor: isSelected ? '#059669' : recommended ? '#1565c0' : '#1976d2',
              borderColor: isSelected ? '#059669' : recommended ? '#1565c0' : '#1976d2',
              color: 'white',
            },
          }}
        >
          {isSelected ? `✓ ${category.name}` : ` ${category.name}`}
        </Button>
      </CardContent>
    </Card>
  );
};

const RegisterCategorySelection = () => {
  const navigate = useNavigate();
  const { registerCarwash, getRegisterData, canProceed } = useRegister();
  const [categories, setCategories] = useState<PlanCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<PlanCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recommendedCategoryName, setRecommendedCategoryName] = useState<string>('');
  
  const steps = [
    'Endereço',
    'Responsável',
    'Negócio',
    'Explicação',
    'Pesquisa',
    'Categoria',
    'Login',
    'Confirmação'
  ];

  useEffect(() => {
    if (!canProceed()) {
      navigate("/parceiros");
      return;
    }

    // Verificar se tem dados da pesquisa, se não tiver, redirecionar para a pesquisa
    const { survey } = getRegisterData();
    if (!survey) {
      navigate("/register/survey");
      return;
    }

    fetchCategories();
  }, []);

  const calculateScore = (surveyData: any) => {
    const { tipoLavagem, servicosAdicionais, areaEspera, bebidas } = surveyData;
    let score = 0;
    
    // Tipo de lavagem
    const lavagemScores: { [key: string]: number } = {
      'padrao': 1.0,
      'detalhada': 2.0,
      'avancada': 2.0,
      'exclusiva': 3.0
    };
    score += lavagemScores[tipoLavagem] || 0;
    
    // Serviços adicionais
    const servicosScores: { [key: string]: number } = {
      'cera-pretinho': 0.1,
      'hidratacao-couro': 0.3,
      'higienizacao-interna': 0.4,
      'polimento': 0.5,
      'estetica-basica': 0.6,
      'vitrificacao': 0.8,
      'higienizacao-premium': 1.0,
      'diferenciais-exclusivos': 1.2
    };
    
    servicosAdicionais.forEach((servico: string) => {
      score += servicosScores[servico] || 0;
    });
    
    // Área de espera
    const areaScores: { [key: string]: number } = {
      'nao-possui': -2.0,
      'espaco-sentar': 1.0,
      'estrutura-completa': 3.0,
      'climatizada': 5.0
    };
    score += areaScores[areaEspera as string] || 0;
    
    // Bebidas
    const bebidasScores: { [key: string]: number } = {
      'nao-oferece': -2.0,
      'agua': 0.2,
      'cafe': 0.3,
      'cha': 0.4,
      'diferenciais': 0.8
    };
    
    bebidas.forEach((bebida: string) => {
      score += bebidasScores[bebida as string] || 0;
    });
    
    return score;
  };

  const getScoreRange = (score: number) => {
    if (score >= 10.0) return 'Ultra';
    if (score >= 8.0) return 'Elite';
    if (score >= 2.0) return 'Prime';
    return 'Flex';
  };

  const getAvailableCategories = (score: number, allCategories: PlanCategory[]) => {
    const range = getScoreRange(score);
    
    console.log(`🎯 PONTUAÇÃO CALCULADA: ${score.toFixed(2)}`);
    console.log(`📊 FAIXA: ${range}`);
    
    if (score >= 10.0) {
      // Ultra: mostra todas
      console.log("📋 ULTRA: Mostrando todas as categorias");
      return allCategories;
    } else if (score >= 8.0) {
      // Elite: esconde Ultra
      console.log("📋 ELITE: Escondendo Ultra");
      return allCategories.filter(cat => !cat.name.toLowerCase().includes('ultra'));
    } else if (score >= 2.0) {
      // Prime: mostra apenas Prime e Flex
      console.log("📋 PRIME: Mostrando apenas Prime e Flex");
      return allCategories.filter(cat => 
        cat.name.toLowerCase().includes('prime') || 
        cat.name.toLowerCase().includes('flex')
      );
    } else {
      // Flex: mostra apenas Flex
      console.log("📋 FLEX: Mostrando apenas Flex");
      return allCategories.filter(cat => 
        cat.name.toLowerCase().includes('flex')
      );
    }
  };

  const getRecommendedCategory = (surveyData: any) => {
    const score = calculateScore(surveyData);
    const range = getScoreRange(score);
    
    console.log("🔍 DADOS DA PESQUISA:", surveyData);
    console.log(`🎯 PONTUAÇÃO: ${score.toFixed(2)} - FAIXA: ${range}`);
    
    // Retorna a categoria principal da faixa
    const categoryMap = {
      'Ultra': 'Wash Ultra',
      'Elite': 'Wash Elite', 
      'Prime': 'Wash Prime',
      'Flex': 'Wash Pro e Wash Flex'
    };
    
    const recommended = categoryMap[range] || 'Wash Prime';
    console.log(`✅ RECOMENDAÇÃO: ${recommended} (${range})`);
    return recommended;
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}plan-categories/public`);
      
      if (!response.ok) {
        throw new Error('Erro ao carregar categorias');
      }

      const data = await response.json();
      
      // Definir a ordem desejada das categorias
      const categoryOrder = [
        'Wash Pro e Wash Flex',
        'WashPrime', 
        'Wash Prime',
        'Wash Elite',
        'Wash Ultra'
      ];
      
      // Ordenar as categorias conforme a ordem definida
      const sortedCategories = data.sort((a: PlanCategory, b: PlanCategory) => {
        const indexA = categoryOrder.indexOf(a.name);
        const indexB = categoryOrder.indexOf(b.name);
        return indexA - indexB;
      });
      
      setCategories(sortedCategories);
      
      // Obter dados da pesquisa e calcular pontuação
      const { survey } = getRegisterData();
      console.log("📋 CATEGORIAS DISPONÍVEIS:", sortedCategories.map((cat: PlanCategory) => cat.name));
      
      const score = calculateScore(survey);
      const availableCategories = getAvailableCategories(score, sortedCategories);
      console.log("📋 CATEGORIAS FILTRADAS:", availableCategories.map((cat: PlanCategory) => cat.name));
      
      const recommendedCategoryName = getRecommendedCategory(survey);
      console.log("🎯 CATEGORIA RECOMENDADA:", recommendedCategoryName);
      
      // Salvar o nome da categoria recomendada no estado
      setRecommendedCategoryName(recommendedCategoryName);
      console.log("🏷️ CATEGORIA MARCADA COMO RECOMENDADA:", recommendedCategoryName);
      
      const recommendedCategory = availableCategories.find((cat: PlanCategory) => 
        cat.name === recommendedCategoryName || cat.name === 'WashPrime'
      );
      
      if (recommendedCategory) {
        console.log("✅ CATEGORIA SELECIONADA:", recommendedCategory.name);
        // Mostrar apenas as categorias disponíveis para a faixa de pontuação
        setCategories(availableCategories);
        setSelectedCategory(recommendedCategory);
      } else if (availableCategories.length > 0) {
        console.log("⚠️ CATEGORIA PADRÃO SELECIONADA:", availableCategories[0].name);
        // Usar a primeira categoria disponível na faixa
        setCategories(availableCategories);
        setSelectedCategory(availableCategories[0]);
      }
    } catch (error) {
      console.error('Erro ao buscar categorias:', error);
      setError('Erro ao carregar categorias. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (category: PlanCategory) => {
    setSelectedCategory(category);
  };

  const handleContinue = () => {
    if (!selectedCategory) {
      setError('Por favor, selecione uma categoria');
      return;
    }

    // Salvar a categoria selecionada no contexto
    const currentData = getRegisterData();
    if (!currentData.carwash?.phone) {
      setError('Dados do telefone não encontrados. Volte à página anterior.');
      return;
    }
    
    const carwashData = {
      phone: currentData.carwash.phone,
      cnpj: currentData.carwash.cnpj,
      car_wash_name: currentData.carwash.car_wash_name,
      company_name: currentData.carwash.company_name,
      category_id: selectedCategory.id,
    };
    
    registerCarwash(carwashData);

    // Salvar pré-cadastro na etapa 5 (Categoria)
    const formData = getRegisterData();
    if (formData.carwash) {
      savePreRegistration(5, {
        ...formData,
        carwash: { ...formData.carwash, category_id: selectedCategory.id },
      });
    }

    // Navegar para a próxima etapa
    navigate("/register/signup");
  };

  const handleBack = () => {
    navigate("/register/survey");
  };

  if (loading) {
    return (
      <div className="Register">
        <RegisterHeader />
        <Container maxWidth="md" className="MainContainer">
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '50vh',
            gap: 2
          }}>
            
            <Typography variant="body1" color="text.secondary">
              Carregando categorias...
            </Typography>
          </Box>
        </Container>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%)',
      color: '#0f172a'
    }}>
      {/* Header */}
      <RegisterHeader />
      <header style={{ 
        position: 'relative', 
        backgroundColor: 'white', 
        borderBottom: '1px solid #e2e8f0',
        paddingTop: '80px'
      }}>
        
        <div style={{ 
          position: 'relative', 
          maxWidth: '1280px', 
          margin: '0 auto', 
          padding: '32px 16px' 
        }}>
          
          <RegisterSteps currentStep={6} steps={steps} />

          <div style={{ textAlign: 'center', maxWidth: '1024px', margin: '0 auto' }}>
            <Typography variant="h3" sx={{ 
              fontSize: { xs: '2rem', md: '2rem', lg: '2rem' },
              fontWeight: 'bold',
              lineHeight: 1.2,
              marginBottom: 2,
              color: '#0f172a'
            }}>
              Categorias disponíveis para seu <span style={{ color: '#1976d2' }}>lava-rápido</span>
            </Typography>

            <Typography variant="h6" sx={{ 
              marginTop: 2,
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              color: '#64748b',
              maxWidth: '768px',
              margin: '16px auto 0',
              lineHeight: 1.6
            }}>
              Com base nos serviços e estrutura que você oferece, estas são as categorias adequadas para o seu perfil de negócio.
            </Typography>

            <Box sx={{ 
              marginTop: 3,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
              padding: '12px 24px',
              borderRadius: '50px',
              border: '1px solid #bbdefb'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                💡 Dica: otimize o lucro combinando <strong>fluxo</strong> + <strong>upsell</strong>
              </Typography>
            </Box>
          </div>
        </div>
      </header>

      {/* Categorias de Repasse */}
      <section style={{ padding: '48px 0', backgroundColor: '#f8fafc' }}>
        <Container maxWidth="xl">          
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <Typography variant="h4" sx={{ 
              fontSize: { xs: '1.5rem', md: '2rem' },
              fontWeight: 'bold',
              marginBottom: 2,
              color: '#0f172a'
            }}>
              Escolha sua categoria
            </Typography>
            <Typography variant="h6" sx={{ 
              fontSize: '1.1rem',
              color: '#64748b',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Selecione a categoria que melhor se adapta ao seu negócio
            </Typography>
          </div>

          {error && (
            <Alert severity="error" sx={{ mb: 3, maxWidth: '800px', margin: '0 auto 24px' }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3} sx={{ maxWidth: '1200px', margin: '0 auto', paddingTop: 2 }}>
            {categories.map((category) => (
              <Grid item xs={12} sm={6} lg={3} key={category.id}>
                <CategoryCard
                  category={category}
                  isSelected={selectedCategory?.id === category.id}
                  onSelect={() => handleCategorySelect(category)}
                  recommended={category.name === recommendedCategoryName} // Recomendada baseada na pontuação
                />
              </Grid>
            ))}
          </Grid>

          <div style={{ marginTop: 48, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ 
              color: '#64748b',
              maxWidth: '512px',
              margin: '0 auto',
              lineHeight: 1.6
            }}>
              É a mesma lógica de qualquer negócio: você investe na entrada (CAC) porque o lucro vem do{" "}
              <em style={{ fontWeight: '600', color: '#0f172a' }}>que você vende depois</em>.
            </Typography>
            
            <Typography variant="body2" sx={{ 
              color: '#64748b',
              marginTop: 3,
              fontSize: '1rem'
            }}>
              Ficou alguma dúvida?{" "}
              <a 
                href="/fale-conosco" 
                style={{ 
                  color: '#1976d2', 
                  textDecoration: 'none',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                Fale Conosco
              </a>
            </Typography>
          </div>
        </Container>
      </section>

      {/* CTA Final */}
      <footer style={{ 
        position: 'sticky',
        bottom: 0,
        zIndex: 10,
        borderTop: '1px solid #e2e8f0',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(8px)'
      }}>
        <Container maxWidth="xl">
          <Box sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            padding: '16px 0'
          }}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography variant="h6" sx={{ fontWeight: '600', color: '#0f172a' }}>
                Escolheu sua categoria?
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Confirme sua seleção e comece a receber clientes.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 16px',
                  borderColor: '#d1d5db',
                  color: '#374151',
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#f9fafb',
                    borderColor: '#9ca3af',
                  },
                }}
              >
                ← Voltar
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  ReactGA.event("continue_category_click", {
                    event: 'continue_category_click',
                    button_id: 'continue_category',
                    page_path: window.location.pathname,
                    category_id: selectedCategory?.id,
                    category_name: selectedCategory?.name
                  });
                  handleContinue();
                }}
                disabled={!selectedCategory}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '12px 24px',
                  borderRadius: 3,
                  fontWeight: '600',
                  textTransform: 'none',
                  fontSize: '1rem',
                  backgroundColor: selectedCategory ? '#1976d2' : '#d1d5db',
                  color: selectedCategory ? 'white' : '#9ca3af',
                  '&:hover': {
                    backgroundColor: selectedCategory ? '#1565c0' : '#d1d5db',
                  },
                  '&:disabled': {
                    backgroundColor: '#d1d5db',
                    color: '#9ca3af',
                  },
                }}
              >
                {selectedCategory ? `Confirmar ${selectedCategory.name}` : 'Aguarde...'}
                →
              </Button>
            </Box>
          </Box>
        </Container>
      </footer>
    </div>
  );
};

export default RegisterCategorySelection;